// Decide where each segment of a recording should be read from when the LOCAL
// copy is incomplete.
//
// A segment that never reached IndexedDB (storage quota, a transaction abort, a
// session that lapsed mid-recording) used to be lost outright: the assembler
// stitched whatever rows survived, uploaded the gapped MP3, warned once, and then
// deleted the rows — so the encounter kept a transcript with a hole in it and the
// audio was gone. It need not be: useRecorder streams every segment to the
// server's "Recording - Store Segment" backstop as it is captured (an additive
// durability copy, A2), so the missing indices can usually be pulled back BEFORE
// the stitch.
//
// This module owns only the decision — which indices are missing, and which of
// them the server can supply. All I/O (IndexedDB reads, the manifest call, the
// byte pulls) stays in useIndexDb, so the load-bearing logic is unit-testable.
//
// LOCAL ALWAYS WINS: a local row is the same codec the recorder produced and
// costs no network, so the server copy is only ever a filler for a hole.

// The indices in [0, expectedTotal) that no local row covers, ascending.
// expectedTotal comes from the recorder's own count (the final segment's `total`,
// or max(index)+1 for a finalless set), so it counts the segments that FAILED to
// persist as well as the ones that made it.
export function missingIndices(presentIndices, expectedTotal) {
  if (typeof expectedTotal !== "number" || !Number.isFinite(expectedTotal) || expectedTotal <= 0) return [];
  const present = new Set(Array.isArray(presentIndices) ? presentIndices : []);
  const missing = [];
  for (let i = 0; i < expectedTotal; i++) if (!present.has(i)) missing.push(i);
  return missing;
}

// Build the ordered source list the stitcher walks.
//
//   localSegs      [{ key, index }]                    — rows in this browser's IndexedDB
//   serverSegments [{ index, fileName, mimeType }]      — the manifest's backstop copies
//   expectedTotal  number                               — segments the recorder said it made
//
// Returns:
//   sources    ordered by index: { kind: "local", key, index }
//                             or { kind: "server", fileName, mimeType, index }
//   unfilled   indices with NO copy anywhere — a genuine, unrecoverable hole
//   rescuable  indices missing locally that the server CAN supply (⊂ filled)
//
// A duplicate index (a doubled manifest row, or a local row written twice) would
// stitch the same 10s of audio twice, so each index appears exactly once.
export function buildSegmentSources({ localSegs, serverSegments, expectedTotal }) {
  const byIndex = new Map();
  for (const seg of Array.isArray(localSegs) ? localSegs : []) {
    if (!seg || typeof seg.index !== "number") continue;
    if (!byIndex.has(seg.index)) byIndex.set(seg.index, { kind: "local", key: seg.key, index: seg.index });
  }
  const localIndices = [...byIndex.keys()];
  const missing = missingIndices(localIndices, expectedTotal);

  const serverByIndex = new Map();
  for (const seg of Array.isArray(serverSegments) ? serverSegments : []) {
    if (!seg || typeof seg.index !== "number" || !seg.fileName) continue;
    if (!serverByIndex.has(seg.index)) serverByIndex.set(seg.index, seg);
  }

  const rescuable = [];
  const unfilled = [];
  for (const idx of missing) {
    const seg = serverByIndex.get(idx);
    if (!seg) {
      unfilled.push(idx);
      continue;
    }
    rescuable.push(idx);
    byIndex.set(idx, { kind: "server", fileName: seg.fileName, mimeType: seg.mimeType, index: idx });
  }

  // Ascending by index — the stitcher relies on the order for both the audio
  // sequence and its trailing-fragment rule (only the LAST entry may be a
  // header-only stop-boundary tail).
  const sources = [...byIndex.values()].sort((a, b) => a.index - b.index);
  return { sources, unfilled, rescuable };
}
